import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MentorshipIcon from '@mui/icons-material/School';
import TeamIcon from '@mui/icons-material/Group';
import FormIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AdminIcon from '@mui/icons-material/AdminPanelSettings';
import GeneralIcon from '@mui/icons-material/Notifications';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArchiveIcon from '@mui/icons-material/Archive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BadgeIcon } from '../../constants/icons';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { NotificationDTO, NotificationArea } from '../../../../shared/types';
import {
    useNotifications,
    useMarkNotificationRead,
    useMarkAllNotificationsRead,
    useArchiveNotification
} from '../../hooks/useNotifications';

// ─── Area icon map ────────────────────────────────────────────────────────────
const AREA_ICONS: Record<NotificationArea, React.ReactElement> = {
    mentorship: <MentorshipIcon fontSize="small" />,
    badge: <BadgeIcon fontSize="small" />,
    team: <TeamIcon fontSize="small" />,
    form: <FormIcon fontSize="small" />,
    assessment: <AssessmentIcon fontSize="small" />,
    admin: <AdminIcon fontSize="small" />,
    general: <GeneralIcon fontSize="small" />
};

const AREA_COLORS: Record<NotificationArea, 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'error' | 'default'> = {
    mentorship: 'primary',
    badge: 'warning',
    team: 'info',
    form: 'secondary',
    assessment: 'secondary',
    admin: 'error',
    general: 'default'
};

// ─── Relative time helper ─────────────────────────────────────────────────────
function relativeTime(date: Date | string): string {
    const now = Date.now();
    const then = new Date(date).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDays = Math.floor(diffHr / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Single notification item ─────────────────────────────────────────────────
interface NotificationItemProps {
    notification: NotificationDTO;
    onRead: (id: string) => void;
    onArchive: (id: string) => void;
    onExpand: (notification: NotificationDTO) => void;
    onClose: () => void;
}

const NotificationItem = ({ notification, onRead, onArchive, onExpand, onClose }: NotificationItemProps) => {
    const navigate = useNavigate();
    const isUnread = notification.status === 'unread';
    const id = notification._id!;

    const handleNavigate = () => {
        if (isUnread) onRead(id);
        if (notification.actionUrl) { navigate(notification.actionUrl); onClose(); }
    };

    const truncated = notification.message.length > 100
        ? notification.message.slice(0, 100) + '…'
        : notification.message;

    return (
        <Box
            sx={{
                px: 2,
                py: 1.5,
                borderLeft: isUnread ? '3px solid' : '3px solid transparent',
                borderLeftColor: isUnread ? 'primary.main' : 'transparent',
                bgcolor: isUnread ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                cursor: 'pointer',
                position: 'relative'
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {/* Area icon or actor avatar */}
                <Avatar
                    src={notification.actorProfile?.profilePicture}
                    sx={{ width: 32, height: 32, bgcolor: `${AREA_COLORS[notification.area]}.main`, mt: 0.25 }}
                >
                    {notification.actorProfile?.displayName?.charAt(0) ?? AREA_ICONS[notification.area]}
                </Avatar>

                {/* Content */}
                <Box flex={1} minWidth={0} onClick={handleNavigate}>
                    <Stack direction="row" alignItems="center" spacing={0.5} mb={0.25}>
                        <Typography
                            variant="body2"
                            fontWeight={isUnread ? 700 : 400}
                            noWrap
                            sx={{ flex: 1 }}
                        >
                            {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {relativeTime(notification.createdAt!)}
                        </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {truncated}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <Chip
                            label={notification.area}
                            size="small"
                            color={AREA_COLORS[notification.area]}
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                        />
                        {notification.message.length > 100 && (
                            <Button
                                size="small"
                                sx={{ height: 18, fontSize: 10, minWidth: 0, px: 0.5 }}
                                onClick={e => { e.stopPropagation(); onExpand(notification); }}
                            >
                                Read more
                            </Button>
                        )}
                        {notification.actionUrl && notification.actionLabel && (
                            <Button
                                size="small"
                                variant="outlined"
                                endIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                                sx={{ height: 20, fontSize: 10, minWidth: 0, px: 0.75 }}
                                onClick={e => { e.stopPropagation(); handleNavigate(); }}
                            >
                                {notification.actionLabel}
                            </Button>
                        )}
                    </Stack>
                </Box>

                {/* Archive button */}
                <Tooltip title="Dismiss">
                    <IconButton
                        size="small"
                        sx={{ mt: -0.5, opacity: 0, '.MuiBox-root:hover &': { opacity: 1 } }}
                        onClick={e => { e.stopPropagation(); onArchive(id); }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

// ─── Notification sidebar drawer ──────────────────────────────────────────────
type TabValue = 'all' | 'unread';

interface NotificationSidebarProps {
    open: boolean;
    onClose: () => void;
    crewSlug: string;
}

export const NotificationSidebar = ({ open, onClose, crewSlug }: NotificationSidebarProps) => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<TabValue>('unread');
    const [expandedNotification, setExpandedNotification] = useState<NotificationDTO | null>(null);

    // Always land on "unread" when opening the sidebar so new notifications are visible
    const prevOpenRef = React.useRef(false);
    if (open && !prevOpenRef.current) {
        setTab('unread');
    }
    prevOpenRef.current = open;

    // Both queries must always be mounted so SSE invalidation refetches the inactive
    // tab immediately — without this, switching to "Unread" after an SSE arrives shows
    // stale empty data while the newly-active query catches up in the background.
    const { data: allData, isLoading: allLoading, isFetching: allFetching } =
        useNotifications(crewSlug, {}, open);
    const { data: unreadData, isLoading: unreadLoading, isFetching: unreadFetching } =
        useNotifications(crewSlug, { status: 'unread' as const }, open);

    const data = tab === 'unread' ? unreadData : allData;
    const isLoading = tab === 'unread' ? unreadLoading : allLoading;
    const isFetching = tab === 'unread' ? unreadFetching : allFetching;
    const notifications = data?.data ?? [];

    const markRead = useMarkNotificationRead(crewSlug);
    const markAllRead = useMarkAllNotificationsRead(crewSlug);
    const archive = useArchiveNotification(crewSlug);

    const unreadCount = notifications.filter(n => n.status === 'unread').length;

    const handleRead = (id: string) => markRead.mutate(id);
    const handleArchive = (id: string) => archive.mutate(id);
    const handleMarkAllRead = () => markAllRead.mutate();

    const handleClose = () => {
        setExpandedNotification(null);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: { xs: '100vw', sm: 400 }, display: 'flex', flexDirection: 'column' } }}
        >
            {expandedNotification ? (
                // ── Detail view (inline, no separate Dialog) ──────────────────
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ px: 1.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
                        spacing={0.5}
                    >
                        <Tooltip title="Back to list">
                            <IconButton size="small" onClick={() => setExpandedNotification(null)}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
                            <Chip
                                label={expandedNotification.area}
                                size="small"
                                color={AREA_COLORS[expandedNotification.area]}
                                variant="outlined"
                            />
                            <Typography variant="subtitle2" noWrap flex={1}>
                                {expandedNotification.title}
                            </Typography>
                        </Stack>
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                        {expandedNotification.actorProfile && (
                            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                <Avatar
                                    src={expandedNotification.actorProfile.profilePicture}
                                    sx={{ width: 28, height: 28 }}
                                >
                                    {expandedNotification.actorProfile.displayName?.charAt(0)}
                                </Avatar>
                                <Typography variant="caption" color="text.secondary">
                                    {expandedNotification.actorProfile.displayName} · {relativeTime(expandedNotification.createdAt!)}
                                </Typography>
                            </Stack>
                        )}
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 3 }}>
                            {expandedNotification.message}
                        </Typography>
                        {expandedNotification.actionUrl && expandedNotification.actionLabel && (
                            <Button
                                variant="contained"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => {
                                    navigate(expandedNotification.actionUrl!);
                                    handleClose();
                                }}
                            >
                                {expandedNotification.actionLabel}
                            </Button>
                        )}
                    </Box>
                </>
            ) : (
                // ── List view ─────────────────────────────────────────────────
                <>
                    {/* Header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Typography variant="h6" fontWeight={700}>Notifications</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            {unreadCount > 0 && (
                                <Tooltip title="Mark all as read">
                                    <IconButton
                                        size="small"
                                        onClick={handleMarkAllRead}
                                        disabled={markAllRead.isPending}
                                    >
                                        <DoneAllIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {/* Tabs */}
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant="fullWidth"
                        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
                    >
                        <Tab label="All" value="all" sx={{ minHeight: 40, fontSize: 13 }} />
                        <Tab
                            label={unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread'}
                            value="unread"
                            sx={{ minHeight: 40, fontSize: 13 }}
                        />
                    </Tabs>

                    {/* List */}
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {isLoading || (isFetching && notifications.length === 0) ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                                Loading…
                            </Typography>
                        ) : notifications.length === 0 ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ p: 4, gap: 1 }}>
                                <GeneralIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {tab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                                </Typography>
                            </Stack>
                        ) : (
                            <>
                                {notifications.map((notif, idx) => (
                                    <React.Fragment key={notif._id}>
                                        <NotificationItem
                                            notification={notif}
                                            onRead={handleRead}
                                            onArchive={handleArchive}
                                            onExpand={n => {
                                                if (n.status === 'unread') handleRead(n._id!);
                                                setExpandedNotification(n);
                                            }}
                                            onClose={handleClose}
                                        />
                                        {idx < notifications.length - 1 && (
                                            <Divider sx={{ mx: 2 }} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    {data && data.total > notifications.length && (
                        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing {notifications.length} of {data.total}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Drawer>
    );
};

export default NotificationSidebar;
